/** Public landing page for ad traffic (design pass 5, ADR-0026). Honest proof only: what the platform does and measures. */
export default {
  brand: "Funded Lime",
  buy: "Start the challenge",
  nav: { programs: "Programs", execution: "Execution", docs: "Documents", faq: "FAQ", cabinet: "Cabinet", signIn: "Sign in", start: "Open an account", language: "Language", menu: "Menu" },
  hero: {
    titleA: "Squeeze",
    titleB: "the market",
    year: "(’26)",
    about: "(About)",
    aboutText: "A simulated prop desk on real market data. Every order meets the live book the moment it arrives and fills in 10–30 ms.",
    badge: "Policy v{v}",
    avail1: "Filled in 10–30 ms",
    avail2: "no added latency",
    outNow: "Live now",
    outTitle: "Crypto 24/7",
    outTag: "[Live]",
    feed: "Engine feed",
    markets: "Markets",
    sources: "Real-time market data · crypto · FX · indices",
    live: "Live from the engine",
    title: "Prop trading on a real engine",
    lead: "Every order is filled the moment it arrives against the live order book, and every fill keeps a snapshot of the market. Crypto around the clock, FX and CME futures.",
    primary: "Choose a program",
    secondary: "Create an account",
    btc: "BTC last trade",
    latency: "Added latency, p50",
    fills: "Fills, 7 days",
    policy: "Execution policy",
    tape: "Last trades",
    ms: "{v} ms",
  },
  bar: { since: "Since", year: "2026", scroll: "Scroll down", live: "Live in the book" },
  statement: {
    text: "We don’t sell a dream. We sell a fair fill: every order meets the live book, every fill keeps its receipt, and the rules you buy are frozen by their hash.",
    sub: "Simulated accounts on real market data.",
    subMuted: "Built for traders who read the tape.",
  },
  slogans: {
    s1: "Now is the time to start",
    s2: "Now is the time to change",
    s3: "Trade the plan, not the hope",
    s4: "Discipline is freedom",
    s5: "Small risk, long game",
    s6: "Earn your capital",
  },
  quotes: {
    money: { text: "Money follows discipline. Never the other way round.", by: "— A rule of the desk" },
    start: { text: "Every funded trader was once a beginner who didn’t quit.", by: "— A rule of the desk" },
  },
  board: {
    title: "Execution, in public",
    lead: "Aggregated from the market snapshot the engine stores with every fill over the last 7 days. Nothing is selected or edited.",
    live: "Live",
    updated: "Updated {time}",
    fills: "Fills in 7 days",
    accounts: "accounts trading",
    latency: "Added latency, p50 / p99",
    quoteAge: "Quote age at fill, p50 / p99",
    walked: "Fills walked through the visible book",
    policy: "Execution policy",
    lastFill: "Last fill {time}",
    ms: "{a} / {b} ms",
    withheld: "Too few fills in the window to publish figures.",
    loading: "Loading…",
    receipt: "Every fill keeps its receipt",
    receiptLead: "“Why here” on any fill: the book it hit, mark, last trade, quote age, latency, fill model and policy version.",
  },
  spec: {
    title: "Choose your evaluation",
    lead: "Every parameter before you pay. The rules you buy are frozen and identified by their hash.",
    market: "Market",
    markets: { crypto: "Crypto perpetuals", fx: "FX & metals", futures: "CME futures" },
    desc: {
      crypto: "BTC, ETH and SOL perpetuals on live market data. The book never closes.",
      fx: "EUR/USD, GBP/USD, USD/JPY and gold. The day ends at 17:00 New York.",
      futures: "ES, NQ, CL and GC on the CME session.",
    },
    steps: "Evaluation",
    step1: "1 step",
    step2: "2 steps",
    size: "Account size",
    fee: "Evaluation fee",
    target: "Profit target",
    dll: "Daily loss limit",
    ml: "Maximum loss",
    days: "Min. trading days",
    split: "Profit split",
    dayEnds: "Trading day ends",
    version: "Rules version",
    oneTime: "one-time",
    perMonth: "/ month",
    yourTime: "{time} your time",
    select: "Start with {size}",
    footnote: "Launch prices. A new rules version applies only to new purchases.",
    none: "No program for this combination yet.",
  },
  platform: {
    title: "A terminal built for this desk",
    lead: "Chart, order book, time and sales and an order ticket that sizes the order from your risk. On live market data.",
    f1: { t: "Live book and tape", d: "Depth and every trade as it prints." },
    f2: { t: "Size from risk", d: "Set the risk in dollars, the ticket does the rest." },
    f3: { t: "Brackets with the entry", d: "Take profit and stop loss go in with the order." },
    f4: { t: "“Why here”", d: "The market at the moment of every fill." },
    caption: "The terminal on live market data.",
  },
  how: {
    title: "From evaluation to payout",
    s1: { line: "Choose a program", note: "Crypto, FX or CME futures. One price, one rule book, frozen at purchase." },
    s2: { line: "Trade the live book", note: "Real prices and depth; a market order walks the book." },
    s3: { line: "Hit the target within the rules", note: "Daily and maximum loss are checked on every tick, on equity." },
    s4: { line: "Receive up to 90%", note: "After verification, to a screened wallet, approved by two people." },
  },
  docs: {
    title: "Read before you trade",
    lead: "What governs your account is written down, versioned and public.",
    execution: { name: "Execution policy", meta: "v1.3 · full-book fills, 0.75 % fee + 0.25 % leverage fee, x1–x30" },
    rules: { name: "Program rules", meta: "Versioned · SHA-256 per version" },
    risk: { name: "Risk disclosure", meta: "Simulated trading, no real market access" },
    terms: { name: "Terms of Service", meta: "Evaluation fees, funded stage, payouts, KYC" },
    payouts: { name: "Payout policy", meta: "When and how a funded account pays out" },
    refunds: { name: "Payments and refunds", meta: "Crypto payments, refunds before the first trade" },
    prohibited: { name: "Prohibited practices", meta: "Hedging, multi-accounting, copy trading, arbitrage" },
    privacy: { name: "Privacy and cookies", meta: "What we keep and why; necessary cookies only" },
    open: "Open",
  },
  faq: {
    title: "Questions",
    real: { q: "Is this real money?", a: "Trading is simulated: accounts are virtual and orders are filled by our engine against real market data. The fee you pay is real, and so are payouts on funded accounts — paid from the company's own funds under the program rules." },
    fills: { q: "How are my orders filled?", a: "By a published execution policy (v1.3): no added latency — an order meets the live book on arrival and fills in 10–30 ms. A market order sweeps the whole book: only a fifth of each level's displayed size is available to it and the rest fills beyond the book at a progressively worse price, on a spread wider than the raw quote — so large orders pay real slippage and the spread cannot be farmed. Fees: 0.75 % of notional per side plus a 0.25 % leverage fee; leverage x1–x30, your choice. Open “Why here” on any fill." },
    day: { q: "When does my trading day end?", a: "Each program says it: 00:00 UTC for crypto, 17:00 New York for FX, the CME session for futures. The program card shows it in your own time." },
    rules: { q: "Can the rules change after I buy?", a: "No. Your account stays on the program version you bought, identified by its hash. A new version only applies to new purchases." },
    feed: { q: "What if the data feed fails?", a: "A degraded instrument never breaches an account. The decision waits until the feed is live again, and a short price spike is filtered out." },
    payouts: { q: "How do payouts work?", a: "Once funded and verified, you request a payout to a wallet that passed sanctions screening. One person prepares it, another approves it." },
  },
  cta: {
    title: "Now is the time to start",
    lead: "Pick a program, read every rule and open an account in minutes.",
    primary: "Choose a program",
    secondary: "Create an account",
  },
  pics: {
    engine: "Rendering of the matching engine core",
    terminal: "The terminal: live BTC chart, the live order book, time and sales, the order ticket",
    fill: "A fill with its market snapshot: model, latency, quote age, mark, last trade and the book",
  },
  footer: {
    risk: "Risk disclosure. All accounts are simulated; no order reaches a real market. Results in an evaluation do not predict results in live trading. Payouts on funded accounts are paid from the company's own funds under the program rules.",
    note: "Payment in crypto. The account opens as soon as the payment is confirmed.",
    top: "Back to top",
    contact: "hello@fundedlime.com",
    credits: "Charts: our own engine · market data: real-time exchange feeds · pictures: generated",
    year: "2026",
  },
} as const;
